import {
  Collection,
  Loadable,
  Model,
  ModelAttributes,
  ModelDescription,
  collection,
  model,
  modelConfig,
  reference,
} from "@mixitone/oom";
import TournamentEntry from "./TournamentEntry";
import TournamentCompetitionConflict from "./TournamentCompetitionConflict";
import TournamentCompetitionEvent from "./TournamentCompetitionEvent";
import TournamentCompetitionStateSnapshot from "./TournamentCompetitionStateSnapshot";
import TournamentRegistration from "./TournamentRegistration";
import TournamentSession from "./TournamentSession";
import { Database } from "./database.types";
import { setAccountId } from "./concerns/SetAccountId";

const config = modelConfig(
  {
    account_id: {
      type: "string",
    },
    banner_image_id: {
      type: "string",
    },
    club_id: {
      type: "string",
    },
    created_at: {
      type: "datetime",
    },
    description: {
      type: "string",
    },
    entry_type: {
      type: "enum",
      enumType: "singles" as Database["public"]["Enums"]["tournament_entry_type"],
    },
    format: {
      type: "string",
    },
    format_data: {
      type: "json",
      jsonType: {} as Record<string, any>,
    },
    format_settings: {
      type: "json",
      jsonType: {} as Record<string, any>,
    },
    is_deleted: {
      type: "boolean",
    },
    max_entries: {
      type: "number",
    },
    min_entries: {
      type: "number",
    },
    name: {
      type: "string",
    },
    rules: {
      type: "json",
      jsonType: null,
    },
    status: {
      type: "enum",
      enumType: "" as Database["public"]["Enums"]["tournament_session_status"],
    },
    tournament_session_id: {
      type: "string",
    },
    updated_at: {
      type: "datetime",
    },
  },
  {
    entries: collection(() => TournamentEntry as any, "tournament_competition_id"),
    events: collection(() => TournamentCompetitionEvent as any, "tournament_competition_id"),
    conflicts: collection(() => TournamentCompetitionConflict as any, "tournament_competition_id"),
    stateSnapshots: collection(() => TournamentCompetitionStateSnapshot as any, "tournament_competition_id"),
    registrations: collection(() => TournamentRegistration as any, "tournament_competition_id"),
  },
  {
    session: reference(() => TournamentSession as any, "tournament_session_id"),
  },
);

interface TournamentCompetition extends ModelAttributes<typeof config> {
  entries: Collection<TournamentEntry>;
  events: Collection<TournamentCompetitionEvent>;
  conflicts: Collection<TournamentCompetitionConflict>;
  stateSnapshots: Collection<TournamentCompetitionStateSnapshot>;
  registrations: Collection<TournamentRegistration>;
  session: Loadable<TournamentSession>;
}

@setAccountId
@model("tournament_competitions", config)
class TournamentCompetition extends Model<typeof config> {
  static readonly modelDescription: ModelDescription = {
    description:
      "A tournament competition represents a specific event within a tournament session, " +
      "such as 'Mens Doubles A Grade' or 'Mixed Singles'. Each competition has its own set of entries, " +
      "matches, and manages its own registration rules and tournament format. " +
      "Competitions can be singles, doubles, or mixed events with different formats like knockout or round-robin. " +
      "entry_type is either 'singles' or 'doubles'. " +
      "The format field specifies the tournament format type (e.g., 'SingleElimination', 'RoundRobin', 'Swiss', 'SwissWithDivisions', 'Custom'). " +
      "The format_settings field (JSON) contains format-specific configuration options set during competition setup (e.g., division settings, group sizes). " +
      "Use format to identify the format type and format_settings to access format-specific configuration details.",
    availableFields: [
      ...Object.keys(config.fields),
      "requiredPlayersPerEntry",
      "isSetup",
      "isEntryOpen",
      "isRegistration",
      "isActive",
      "isCompleted",
      "isStarted",
      "canEnableSignUp",
      "canStartRegistration",
      "canReopenEntryPeriod",
      "canStart",
      "canComplete",
      "winners",
      "winnersHaveDivisions",
    ],
    relationships: {
      session: "tournament_sessions where id equals this tournament_session_id",
      entries: "tournament_entries where tournament_competition_id equals this id",
      events: "tournament_competition_events where tournament_competition_id equals this id",
      conflicts: "tournament_competition_conflicts where tournament_competition_id equals this id",
      registrations: "tournament_registrations where tournament_competition_id equals this id",
      stateSnapshots: "tournament_competition_state_snapshots where tournament_competition_id equals this id",
    },
  };
  get requiredPlayersPerEntry() {
    return this.entry_type === "doubles" ? 2 : 1;
  }

  get isSetup() {
    return this.status === "setup";
  }

  get isEntryOpen() {
    return this.status === "entry_open";
  }

  get isRegistration() {
    return this.status === "registration";
  }

  get isActive() {
    return this.status === "active";
  }

  get isCompleted() {
    return this.status === "completed";
  }

  get isStarted() {
    return this.isActive || this.isCompleted;
  }

  get canEnableSignUp() {
    return this.isSetup || this.isEntryOpen;
  }

  registrationLink(portalUrl: string) {
    return `${portalUrl}/tournament/${this.session.portal_slug}/register/${this.id}`;
  }

  get canStartRegistration() {
    return this.isSetup || this.isEntryOpen;
  }

  get canReopenEntryPeriod() {
    return this.isRegistration;
  }

  get canStart() {
    return (
      (this.isSetup || this.isEntryOpen || this.isRegistration) &&
      (this.entries?.length ?? 0) >= (this.min_entries ?? 2)
    );
  }

  get canComplete() {
    return this.isActive;
  }

  async startRegistration() {
    if (!this.canStartRegistration) throw new Error("Cannot start registration");

    this.status = "registration";
    await this.save();
  }

  async reopenEntryPeriod() {
    if (!this.canReopenEntryPeriod) throw new Error("Cannot reopen entry period");

    this.status = "entry_open";
    await this.save();
  }

  async start() {
    if (!this.canStart) throw new Error("Cannot start competition");

    this.status = "active";
    await this.save();
  }

  async complete() {
    if (this.isCompleted) throw new Error("Already completed");

    this.status = "completed";
    await this.save();
  }

  canDestroy(): boolean {
    return this.status === "setup";
  }

  async removeEmptyEntries() {
    // Get all entries for this competition
    const entries = await TournamentEntry.query()
      .eq("tournament_competition_id", this.id)
      .neq("id", this.id) // Exclude self-reference
      .preloadCounts("registrations")
      .all();

    // Find entries with no registrations
    const emptyEntries = entries.filter((entry) => entry.registrations.count() === 0);

    // Delete all empty entries concurrently
    return Promise.all(
      emptyEntries.map((entry) => {
        return entry.destroy();
      }),
    );
  }

  get winners() {
    if (!this.format_data) return [];
    if (!this.format_data.winners) return [];
    if (!Array.isArray(this.format_data.winners)) return [];
    if (!this.entries.loaded) {
      throw new Error("Entries not loaded");
    }

    return this.format_data.winners
      .map((winner) => {
        const entry = this.entries.findById(winner[0]);
        if (!entry) return null;
        return {
          entry,
          metadata: winner[1],
        };
      })
      .filter((winner) => winner !== null);
  }

  // Check if any winners have division metadata
  winnersHaveDivisions() {
    return this.winners.some((winner) => winner?.metadata?.division !== undefined);
  }

  // Check if competition has been split into divisions
  hasDivisions(): boolean {
    return !!(
      this.format_data &&
      this.format_data.divisionSplit &&
      typeof this.format_data.divisionSplit === "object" &&
      typeof this.format_data.divisionSplit.round === "number" &&
      typeof this.format_data.divisionSplit.numDivisions === "number"
    );
  }

  // Get the round number where division split occurred (0-indexed)
  getDivisionSplitRound(): number | undefined {
    if (!this.hasDivisions()) return undefined;
    return (this.format_data?.divisionSplit as { round: number; numDivisions: number })?.round;
  }

  // Get the number of divisions
  getNumDivisions(): number | undefined {
    if (!this.hasDivisions()) return undefined;
    return (this.format_data?.divisionSplit as { round: number; numDivisions: number })?.numDivisions;
  }

  get groupsLocked(): boolean {
    return this.format === "RoundRobin" && this.format_data?.groupsLocked === true;
  }

  /**
   * If the format is changed then reset event-sourced competition state
   */
  override async update() {
    const matchAffectingColumns = ["format", "format_settings", "entry_type"] as const;
    const matchAffectingStatuses: this["status"][] = ["setup", "entry_open"];
    const resetMatches =
      matchAffectingStatuses.includes(this.status) &&
      matchAffectingColumns.some((column) => this.changedAttributes.has(column));

    const ret = super.update();

    if (resetMatches) {
      await TournamentCompetitionEvent.query().eq("tournament_competition_id", this.id).deleteAll();
      await TournamentCompetitionConflict.query().eq("tournament_competition_id", this.id).deleteAll();
      await TournamentCompetitionStateSnapshot.query().eq("tournament_competition_id", this.id).deleteAll();
      this.emit("tournament:matchesReset", { competition: this });
    }

    return ret;
  }
}

export default TournamentCompetition;
